/**
 * Временный доступ к API смен, пока методы не попали в `@pro-sto/api-client`.
 * `sdk.gen.ts` и `types.gen.ts` правятся только через `pnpm generate:api`.
 *
 * Будущие operationId: adminShiftsGetList, adminShiftsCreate, adminShiftsGetOne,
 * adminShiftsUpdate, adminShiftsDelete, adminShiftsOpen, adminShiftsClose,
 * adminShiftsMemberStart, adminShiftsMemberFinish, adminShiftsMemberNoShow,
 * adminShiftsMemberReturn, adminShiftsMemberActualTime, adminShiftsMemberRemove.
 * После генерации SDK замена — импорт из `@pro-sto/api-client` и удаление этого файла.
 * Список шаблонов смен — `shiftTemplatesApi.ts` / `adminShiftTemplatesGetList`.
 *
 * В query списка передаётся service_center_uuid (контракт dev API). Оба конца интервала
 * включительно, даты в формате YYYY-MM-DD.
 */
import axios, { type AxiosRequestConfig } from 'axios';
import { OpenAPI } from '@pro-sto/api-client';
import { AppError } from '@/utils/errors';
import type {
  CreateShiftPayload,
  ShiftDetail,
  ShiftsListParams,
  ShiftsListResponse,
  UpdateActualTimePayload,
  UpdateShiftPayload,
  UpdateShiftResponsiblePayload,
} from '@/types/shift.types';

export interface ShiftApiViolationDetail {
  code?: string;
  message?: string;
  member_uuids?: string[];
  booking_uuids?: string[];
}

export class ShiftApiError extends AppError {
  details: string[];
  violationDetails: ShiftApiViolationDetail[];

  constructor(
    message: string,
    status?: number,
    details: string[] = [],
    violationDetails: ShiftApiViolationDetail[] = [],
  ) {
    super(message, status, status);
    this.details = details;
    this.violationDetails = violationDetails;
  }
}

interface ShiftErrorBody {
  message?: unknown;
  error?: unknown;
}

const uuidList = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const ids = value.filter((item): item is string => typeof item === 'string' && Boolean(item));
  return ids.length > 0 ? ids : undefined;
};

const violationDetailsFromBody = (value: unknown): ShiftApiViolationDetail[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const row = item as {
      code?: unknown;
      message?: unknown;
      member_uuids?: unknown;
      booking_uuids?: unknown;
    };
    const message = typeof row.message === 'string' ? row.message : undefined;
    const code = typeof row.code === 'string' ? row.code : undefined;
    if (!message && !code) return [];
    return [
      {
        code,
        message,
        member_uuids: uuidList(row.member_uuids),
        booking_uuids: uuidList(row.booking_uuids),
      },
    ];
  });
};

const detailMessages = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (typeof item === 'string' && item) return [item];
    if (item && typeof item === 'object' && 'message' in item) {
      const message = (item as { message?: unknown }).message;
      return typeof message === 'string' && message ? [message] : [];
    }
    return [];
  });
};

/** Тело Nest: строка message, массив ValidationPipe или error.details у 409 закрытия. */
export const readShiftErrorBody = (
  data: unknown,
): { message: string; details: string[]; violationDetails: ShiftApiViolationDetail[] } => {
  const body = (data && typeof data === 'object' ? data : {}) as ShiftErrorBody;
  const nested =
    body.error && typeof body.error === 'object'
      ? (body.error as { message?: unknown; details?: unknown })
      : null;
  const violationDetails = violationDetailsFromBody(nested?.details);
  const details = detailMessages(nested?.details);
  const rawMessage = body.message ?? nested?.message;
  const message = Array.isArray(rawMessage)
    ? rawMessage.filter((item): item is string => typeof item === 'string' && Boolean(item)).join('; ')
    : typeof rawMessage === 'string'
      ? rawMessage
      : '';

  return {
    message: message || 'Не удалось выполнить запрос',
    details,
    violationDetails,
  };
};

const request = async <T>(config: AxiosRequestConfig): Promise<T> => {
  try {
    const response = await axios.request<T>({
      ...config,
      timeout: 20_000,
      baseURL: OpenAPI.BASE,
      headers: {
        ...(OpenAPI.TOKEN ? { Authorization: `Bearer ${OpenAPI.TOKEN}` } : {}),
        ...config.headers,
      },
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const parsed = readShiftErrorBody(error.response?.data);
      throw new ShiftApiError(parsed.message, status, parsed.details, parsed.violationDetails);
    }
    throw error;
  }
};

export const adminShiftsGetList = (params: ShiftsListParams) =>
  request<ShiftsListResponse>({ method: 'GET', url: '/api/admin/shifts', params });

export const adminShiftsCreate = (data: CreateShiftPayload) =>
  request<ShiftDetail>({ method: 'POST', url: '/api/admin/shifts', data });

export const adminShiftsGetOne = (uuid: string) =>
  request<ShiftDetail>({ method: 'GET', url: `/api/admin/shifts/${uuid}` });

export const adminShiftsUpdate = (uuid: string, data: UpdateShiftPayload) =>
  request<ShiftDetail>({ method: 'PATCH', url: `/api/admin/shifts/${uuid}`, data });

export const adminShiftsUpdateResponsible = (uuid: string, data: UpdateShiftResponsiblePayload) =>
  request<ShiftDetail>({ method: 'PATCH', url: `/api/admin/shifts/${uuid}`, data });

export const adminShiftsOpen = (uuid: string) =>
  request<ShiftDetail>({ method: 'POST', url: `/api/admin/shifts/${uuid}/open` });

export const adminShiftsClose = (uuid: string) =>
  request<ShiftDetail>({ method: 'POST', url: `/api/admin/shifts/${uuid}/close` });

const memberUrl = (uuid: string, memberUuid: string, action: string) =>
  `/api/admin/shifts/${uuid}/members/${memberUuid}/${action}`;

export const adminShiftsMemberStart = (uuid: string, memberUuid: string) =>
  request<ShiftDetail>({ method: 'POST', url: memberUrl(uuid, memberUuid, 'start') });

export const adminShiftsMemberFinish = (uuid: string, memberUuid: string) =>
  request<ShiftDetail>({ method: 'POST', url: memberUrl(uuid, memberUuid, 'finish') });

export const adminShiftsMemberNoShow = (uuid: string, memberUuid: string) =>
  request<ShiftDetail>({ method: 'POST', url: memberUrl(uuid, memberUuid, 'no-show') });

export const adminShiftsMemberReturn = (uuid: string, memberUuid: string) =>
  request<ShiftDetail>({ method: 'POST', url: memberUrl(uuid, memberUuid, 'return') });

export const adminShiftsMemberActualTime = (
  uuid: string,
  memberUuid: string,
  data: UpdateActualTimePayload,
) =>
  request<ShiftDetail>({
    method: 'PATCH',
    url: memberUrl(uuid, memberUuid, 'actual-time'),
    data,
  });

export const adminShiftsMemberRemove = (uuid: string, memberUuid: string) =>
  request<ShiftDetail>({
    method: 'DELETE',
    url: `/api/admin/shifts/${uuid}/members/${memberUuid}`,
  });

/** Удаление пустой смены при отмене редактирования (пока нет в сгенерированном SDK). */
export const adminShiftsDelete = (uuid: string) =>
  request<void>({ method: 'DELETE', url: `/api/admin/shifts/${uuid}` });
